import React, { useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import {
  selectSplitPanelVisible,
  showSplitPanel,
  showExtractPanel,
  selectSelectedPDF,
  selectPages,
  selectMergeExtractedPages,
  selectSplitRanges,
  selectFileName,
  selectFixedRangesVisible,
  selectIsSplitting,
  setSplitting,
} from '../pdf-tool-manage/pdf-slice';
import { useAppDispatch } from '../store/store';
import { SplitPanel } from './pdf-tool-split-panel-component';
import { ExtractPanel } from './pdf-tool-split-extract-panel-component';
import { PDFDocument } from 'pdf-lib';
import { selectBaseUrl } from '../dff/dff-slice';

export const Panels: React.FC<{}> = () => {
  const [destFileUrl, setDestFileUrl] = useState('');
  const [destFileName, setDestFileName] = useState('');
  const [finishedSplitting, setFinishedSplitting] = useState(false);

  const getSelectSplitRangePanelVisible = useSelector(selectSplitPanelVisible);
  const getSelectedPDF = useSelector(selectSelectedPDF);
  const getSelectedPages = useSelector(selectPages);
  const mergeExtractedPages = useSelector(selectMergeExtractedPages);
  const getSelectSplitRanges = useSelector(selectSplitRanges);
  const getSelectFileName = useSelector(selectFileName);
  const isFixedRangesVisible = useSelector(selectFixedRangesVisible);
  const isSplitting = useSelector(selectIsSplitting);
  const baseUrl = useSelector(selectBaseUrl);

  const dispatch = useAppDispatch();

  const originalUrl = (mountId: string, finalName: string): string => {
    return `${baseUrl}/app/storage/${mountId}?path=${encodeURIComponent(getSelectedPDF.path)}/${encodeURIComponent(getSelectFileName)}&name=${encodeURIComponent(
      finalName,
    )}`;
  };

  const folderUrl = (mountId: string): string => {
    return `${baseUrl}/app/storage/${mountId}?path=${getSelectedPDF.path}/${encodeURIComponent(getSelectFileName)}`;
  };

  const createFolder = (mountId: string) => {
    return fetch(`/api/v2/mounts/${mountId}/files/folder?path=${getSelectedPDF.path}`, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: getSelectFileName,
      }),
    });
  };

  const uploadBlob = (blob, fileName, mountId) => {
    return fetch(
      `/content/api/v2/mounts/${mountId}/files/put?path=${getSelectedPDF.path}/${encodeURIComponent(getSelectFileName)}&filename=${encodeURIComponent(fileName)}&info=true`,
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        body: blob,
      },
    )
      .then((response) => response.json())
      .then((r) => {
        return r;
      });
  };

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    dispatch(setSplitting(true));

    const b = await fetch(getSelectedPDF.url)
      .then((r) => {
        return r.arrayBuffer();
      })
      .then((buffer) => {
        return buffer;
      })
      .then((res) => new Uint8Array(res));
    const pdfDocParsed = await PDFDocument.load(b, { ignoreEncryption: true });

    // Create folder and upload files there
    await createFolder(getSelectedPDF.mountId);

    // Split or extract based on which panel is visible
    if (getSelectSplitRangePanelVisible) {
      if (mergeExtractedPages && !isFixedRangesVisible) {
        const pdfDoc = await PDFDocument.create();
        let newBlob: Blob;
        for (const range of getSelectSplitRanges) {
          for (let i = range.fromPage; i <= range.toPage; i++) {
            const [copiedPage] = await pdfDoc.copyPages(pdfDocParsed, [i - 1]);
            pdfDoc.addPage(copiedPage);
            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
            newBlob = blob;
          }
        }

        uploadBlob(
          newBlob,
          `${getSelectFileName}-split.pdf`,
          getSelectedPDF.mountId,
        ).then((r) => {
          setDestFileName(r.name);
          setDestFileUrl(originalUrl(getSelectedPDF.mountId, r.name));
          setFinishedSplitting(true);
          dispatch(setSplitting(false));
        });
      } else {
        await Promise.all(
          getSelectSplitRanges.map(async (range) => {
            const pdfDoc = await PDFDocument.create();
            let newBlob: Blob;
            for (let i = range.fromPage; i <= range.toPage; i++) {
              const [copiedPage] = await pdfDoc.copyPages(pdfDocParsed, [
                i - 1,
              ]);
              pdfDoc.addPage(copiedPage);
              const pdfBytes = await pdfDoc.save();
              const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
              newBlob = blob;
            }

            return uploadBlob(
              newBlob,
              `${getSelectFileName}_${range.fromPage}_${range.toPage}-split.pdf`,
              getSelectedPDF.mountId,
            );
          }),
        ).then((results) => {
          setDestFileUrl(folderUrl(getSelectedPDF.mountId));
          setDestFileName(getSelectFileName);
          setFinishedSplitting(true);
          dispatch(setSplitting(false));
        });
      }
    } else {
      // Extract pages to PDFs
      if (mergeExtractedPages) {
        const pdfDoc = await PDFDocument.create();
        let newBlob: Blob;
        for (const [k, v] of Object.entries(getSelectedPages)) {
          if (v.selected) {
            const [copiedPage] = await pdfDoc.copyPages(pdfDocParsed, [
              parseInt(k) - 1,
            ]);
            // TODO: Move this outside of for-loop and wait for all Promises to finish
            pdfDoc.addPage(copiedPage);
            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
            newBlob = blob;
          }
        }

        uploadBlob(
          newBlob,
          `${getSelectFileName}-extract.pdf`,
          getSelectedPDF.mountId,
        ).then((r) => {
          setDestFileName(r.name);
          setDestFileUrl(originalUrl(getSelectedPDF.mountId, r.name));
          setFinishedSplitting(true);
          dispatch(setSplitting(false));
        });
      } else {
        await Promise.all(
          Object.entries(getSelectedPages).map(async (k) => {
            if (k[1].selected) {
              const pdfDoc = await PDFDocument.create();
              const [copiedPage] = await pdfDoc.copyPages(pdfDocParsed, [
                parseInt(k[0]) - 1,
              ]);
              pdfDoc.addPage(copiedPage);
              const pdfBytes = await pdfDoc.save();
              const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });

              return uploadBlob(
                blob,
                `${getSelectFileName}_${k[0]}-extract.pdf`,
                getSelectedPDF.mountId,
              );
            }
          }),
        ).then((results) => {
          setDestFileUrl(folderUrl(getSelectedPDF.mountId));
          setDestFileName(getSelectFileName);
          setFinishedSplitting(true);
          dispatch(setSplitting(false));
        });
      }
    }
  };

  return (
    <div className="pdf-tool-manage__right-container">
      <div className="pdf-tool-manage__right__title">Split PDF</div>
      <div className="pdf-tool-manage__right__buttons">
        <div
          className={classNames('pdf-tool-manage__right__button-top', {
            'pdf-tool-manage__right__button-top--selected': getSelectSplitRangePanelVisible,
          })}
          onClick={() => {
            dispatch(showSplitPanel());
            setFinishedSplitting(false);
            setDestFileUrl('');
          }}
        >
          Split by range
        </div>
        <div
          className={classNames('pdf-tool-manage__right__button-top', {
            'pdf-tool-manage__right__button-top--selected': !getSelectSplitRangePanelVisible,
          })}
          onClick={() => {
            dispatch(showExtractPanel());
            setFinishedSplitting(false);
            setDestFileUrl('');
          }}
        >
          Extract pages
        </div>
      </div>

      {!isSplitting && !finishedSplitting ? (
        <>
          {getSelectSplitRangePanelVisible ? <SplitPanel /> : <ExtractPanel />}
        </>
      ) : null}

      {destFileUrl ? (
        <>
          <div className="pdf-tool-manage__right__description">
            Splitted file is available here:
          </div>

          <a
            href={destFileUrl}
            target="_blank"
            className="pdf-tool-manage__right__file-link"
          >
            {destFileName}
          </a>
        </>
      ) : null}
      <button
        disabled={isSplitting && !finishedSplitting}
        className={classNames('pdf-tool-manage__right__split-button', {
          'pdf-tool-manage__right__split-button--disabled':
            isSplitting && !finishedSplitting,
        })}
        onClick={(e) => handleClick(e)}
      >
        {isSplitting && !finishedSplitting ? 'Splitting PDF' : 'Split PDF'}
      </button>
    </div>
  );
};
